/**
 * Smart Sizing
 * ------------
 * Reads the product JSON (#size-guide-data) and recommends a size.
 *
 * Layout of this file:
 *   1. CONFIG        – all tunable numbers live here (offsets, tolerances, bounds)
 *   2. ENGINE        – pure functions, no DOM. Exposed as window.SmartSizingEngine for testing
 *   3. DOM / DRAWER  – wiring to the Shopify theme
 */
(function (root) {
  'use strict';

  // ===========================================================================
  // 1. CONFIG
  // ===========================================================================

  const SIZES_ORDER = [
    'XXS', 'XXS/XS', 'XS', 'XS/S', 'S', 'S/M', 'M', 'M/L', 'L', 'L/XL',
    'XL', 'XL/XXL', 'XXL', '2XL', '3XL', '4XL', '5XL', '6XL', 'ONE SIZE', 'UNI'
  ];

  // Metrics that mean "accessory": no height needed, no material/fit/layer adjustment.
  const ACCESSORY_METRICS = ['head', 'hand'];

  // Plausible human height in cm. Anything outside is treated as a typo.
  const MIN_HEIGHT_CM = 100;
  const MAX_HEIGHT_CM = 250;

  /**
   * How garment_properties change the calculation.
   *
   * NOTE: Because per-product body_measurements_cm tables already have ease allowances
   * baked in during the Excel -> JSON conversion, offsets default to 0 to prevent
   * double-counting. Tolerances still adapt dynamically to material and fit.
   */
  const ADJUSTMENTS = {
    material: {
      inelastic:      { offset: 0, tolerance: 4 },
      medium_elastic: { offset: 0, tolerance: 5 },
      elastic:        { offset: 0, tolerance: 6 }
    },
    fit: {
      slim:    { offset: 0, heightTolerance: 3 },
      regular: { offset: 0, heightTolerance: 4 },
      loose:   { offset: 0, heightTolerance: 5 }
    },
    layer: {
      base_layer:  { offset: 0 },
      mid_layer:   { offset: 0 },
      outer_shell: { offset: 0 }
    }
  };

  // Alternative spellings that map onto the keys above (safety net for typos / older data).
  const ALIASES = {
    material: { non_elastic: 'inelastic', rigid: 'inelastic', low_elastic: 'inelastic', high_elastic: 'elastic', stretch: 'elastic' },
    fit: { tight: 'slim', skinny: 'slim', relaxed: 'loose', oversized: 'loose', baggy: 'loose', standard: 'regular' },
    layer: { base: 'base_layer', mid: 'mid_layer', outer: 'outer_shell', outer_layer: 'outer_shell', shell: 'outer_shell' }
  };

  const DEFAULTS = { offset: 0, tolerance: 5, heightTolerance: 4, accessoryTolerance: 2 };
  const MAX_TOTAL_OFFSET = 3;

  // Extra tolerance below the smallest size (e.g. for oversized jackets like Garzeno where S starts at 102 cm).
  const SNAP_BELOW_MIN_CM = 8;

  // Height warnings come from one of two sources:
  //   - a "height" table inside the product's own JSON (body_measurements_cm.height): real garment data,
  //     uses the strict fit-based tolerance above (3-5 cm);
  //   - the built-in universal height table: only a rough guess (it assumes e.g. that every size-XL man
  //     is 188-194 cm), so it warns only for CLEAR extremes. Set to null to switch these warnings off.
  const UNIVERSAL_HEIGHT_TOLERANCE = 12;

  // Sizes whose height band is identical to the size below them (or that are missing from the
  // height table entirely) are "girth-extension" sizes: they get wider, not taller.
  // For those sizes a shorter customer is not warned that the garment is "too long".
  const SUPPRESS_LONG_WARNING_ON_PLATEAU = true;

  // "Looser option": the next size UP, shown as "if you prefer a looser fit, consider X".
  // Only shown when the customer is in the top 30% of the size range (e.g. 97-100 on a 90-100 range,
  // or 97-98 on a 94-98 range) and past the midpoint of the size.
  const ALT_SIZE_TOP_FRACTION = 0.30;
  const ALT_SIZE_MARGIN_CM = 1.5;
  const ALT_SIZE_MARGIN_ACCESSORY_CM = 1.0;

  // ADDED: "between sizes". When the customer's measurement sits within this many cm of their
  // recommended band's edge, computeRecommendation returns `betweenSizes` so the drawer can say
  // "you're between S and M" instead of silently picking a side. Purely additive — it never
  // changes which size is recommended.
  const BETWEEN_MARGIN_CM = 1.5;

  const UNIVERSAL_TABLES = {
    men: {
      height: { XS: [164, 170], S: [170, 176], M: [176, 182], L: [182, 188], XL: [188, 194], XXL: [188, 194], '3XL': [188, 194], '4XL': [194, 198] },
      chest:  { XS: [82, 86],   S: [86, 94],   M: [94, 102],  L: [102, 110], XL: [110, 118], XXL: [118, 129], '3XL': [129, 135], '4XL': [135, 141] },
      waist:  { XS: [70, 74],   S: [74, 82],   M: [82, 90],   L: [90, 98],   XL: [98, 106],  XXL: [106, 117], '3XL': [117, 123], '4XL': [123, 129] },
      hips:   { XS: [85, 88],   S: [89, 96],   M: [97, 104],  L: [105, 112], XL: [113, 122], XXL: [122, 136], '3XL': [136, 142], '4XL': [142, 148] }
    },
    women: {
      height: { XS: [154, 160], S: [160, 168], M: [168, 170], L: [170, 174], XL: [174, 178], XXL: [178, 182], '3XL': [178, 182] },
      chest:  { XS: [78, 86],   S: [86, 94],   M: [94, 102],  L: [102, 113], XL: [113, 125], XXL: [125, 131], '3XL': [131, 137] },
      waist:  { XS: [62, 70],   S: [70, 78],   M: [78, 86],   L: [86, 97],   XL: [97, 109],  XXL: [109, 120], '3XL': [120, 126] },
      hips:   { XS: [86, 94],   S: [94, 102],  M: [102, 110], L: [110, 120], XL: [120, 130], XXL: [130, 140], '3XL': [140, 148] }
    },
    unisex: {
      height: { XS: [158, 170], S: [163, 176], M: [168, 182], L: [175, 188], XL: [182, 194], XXL: [184, 194], '3XL': [186, 194], '4XL': [190, 198] },
      chest:  { XS: [80, 86],   S: [86, 94],   M: [94, 102],  L: [102, 110], XL: [110, 118], XXL: [118, 129], '3XL': [129, 135], '4XL': [135, 141] },
      waist:  { XS: [66, 74],   S: [74, 82],   M: [82, 90],   L: [90, 98],   XL: [98, 106],  XXL: [106, 117], '3XL': [117, 123], '4XL': [123, 129] },
      hips:   { XS: [85, 92],   S: [92, 99],   M: [99, 106],  L: [106, 115], XL: [115, 125], XXL: [125, 136], '3XL': [136, 142], '4XL': [142, 148] }
    }
  };

  // ===========================================================================
  // 2. ENGINE (pure logic, no DOM)
  // ===========================================================================

  function normalizeKey(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  }

  function normalizeSizeKey(rawSize) {
    if (!rawSize) return '';
    const cleaned = String(rawSize).replace(/\.\d+$/, '').trim().toUpperCase();
    if (cleaned === '2XL') return 'XXL';
    if (cleaned === 'ONE SIZE') return 'UNI';
    return cleaned;
  }

  function sanitizeRanges(rawRanges) {
    if (!rawRanges || typeof rawRanges !== 'object') return null;
    const clean = {};
    for (const [k, v] of Object.entries(rawRanges)) {
      const sz = normalizeSizeKey(k);
      if (sz && Array.isArray(v) && v.length === 2 && !isNaN(v[0]) && !isNaN(v[1])) {
        clean[sz] = [Number(v[0]), Number(v[1])];
      }
    }
    return Object.keys(clean).length > 0 ? clean : null;
  }

  function toNumber(value) {
    if (typeof value === 'number') return value;
    if (value == null) return NaN;
    return parseFloat(String(value).replace(',', '.'));
  }

  function sameRange(a, b) {
    return Array.isArray(a) && Array.isArray(b) && a[0] === b[0] && a[1] === b[1];
  }

  function getSortedKeys(ranges) {
    return Object.keys(ranges)
      .filter((size) => SIZES_ORDER.indexOf(size) !== -1)
      .sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b));
  }

  /**
   * Turns garment_properties.{material, fit, layer} into numbers the engine can use.
   * Unknown / missing values fall back to neutral (offset 0) and are reported in `warnings`.
   */
  function resolveAdjustments(props, isAccessory) {
    const result = {
      offset: 0,
      tolerance: isAccessory ? DEFAULTS.accessoryTolerance : DEFAULTS.tolerance,
      heightTolerance: DEFAULTS.heightTolerance,
      applied: { material: null, fit: null, layer: null },
      warnings: []
    };

    // Accessories (hats, gloves...) are measured directly – no ease allowance applies.
    if (isAccessory) return result;

    ['material', 'fit', 'layer'].forEach((group) => {
      const raw = props[group];
      const key = normalizeKey(raw);
      if (!key) return; // property not set -> neutral

      const table = ADJUSTMENTS[group];
      const resolvedKey = table[key] ? key : ALIASES[group][key];
      const entry = resolvedKey ? table[resolvedKey] : null;

      if (!entry) {
        result.warnings.push(`Unknown ${group} "${raw}" – treated as neutral.`);
        return;
      }

      result.applied[group] = resolvedKey;
      result.offset += entry.offset || 0;
      if (entry.tolerance != null) result.tolerance = entry.tolerance;
      if (entry.heightTolerance != null) result.heightTolerance = entry.heightTolerance;
    });

    result.offset = Math.max(-MAX_TOTAL_OFFSET, Math.min(MAX_TOTAL_OFFSET, result.offset));
    return result;
  }

  function findBestSize(value, ranges, maxTolerance) {
    if (!ranges || isNaN(value)) return null;
    const tolerance = maxTolerance == null ? DEFAULTS.tolerance : maxTolerance;

    // 1) Exact match. If several ranges contain the value (overlapping ranges, e.g. XS 77-83 and
    //    S 82-88), pick the size whose range is centred closest to the value; an exact tie goes to
    //    the LARGER size, consistent with the "round up" rule used everywhere else.
    let inRange = null;
    let bestCentreDiff = Infinity;
    for (const [size, range] of Object.entries(ranges)) {
      if (value >= range[0] && value <= range[1]) {
        const centreDiff = Math.abs(value - (range[0] + range[1]) / 2);
        const isLarger = inRange !== null && SIZES_ORDER.indexOf(size) > SIZES_ORDER.indexOf(inRange);
        if (centreDiff < bestCentreDiff || (centreDiff === bestCentreDiff && isLarger)) {
          bestCentreDiff = centreDiff;
          inRange = size;
        }
      }
    }
    if (inRange) return inRange;

    // 2) Between sizes / slightly outside: nearest edge within tolerance (ties round up).
    //    If the measurement is below the smallest available size (e.g. oversized garments),
    //    allow snapping up to SNAP_BELOW_MIN_CM.
    const sortedKeys = getSortedKeys(ranges);
    const smallestKey = sortedKeys[0];

    let bestSize = null;
    let minEdgeDiff = Infinity;
    for (const [size, range] of Object.entries(ranges)) {
      const diff = value < range[0] ? range[0] - value : value - range[1];
      const effectiveTol = (size === smallestKey && value < range[0])
        ? Math.max(tolerance, SNAP_BELOW_MIN_CM)
        : tolerance;

      if (diff <= minEdgeDiff && diff <= effectiveTol) {
        minEdgeDiff = diff;
        bestSize = size;
      }
    }
    return bestSize;
  }

  function getLargerSize(sizeA, sizeB) {
    if (!sizeA) return sizeB;
    if (!sizeB) return sizeA;
    const indexA = SIZES_ORDER.indexOf(normalizeSizeKey(sizeA));
    const indexB = SIZES_ORDER.indexOf(normalizeSizeKey(sizeB));
    return indexB > indexA ? sizeB : sizeA;
  }

  function getMetricRanges(sizeData, metricKey, gender, availableSizes) {
    const rawCustom = sizeData.body_measurements_cm && sizeData.body_measurements_cm[metricKey];
    const customRanges = sanitizeRanges(rawCustom);
    if (customRanges) return customRanges;

    const genderTable = UNIVERSAL_TABLES[gender] || UNIVERSAL_TABLES.men;
    const universalMetric = genderTable && genderTable[metricKey];
    if (!universalMetric) return null;

    if (availableSizes && availableSizes.length > 0) {
      const filtered = {};
      availableSizes.forEach((size) => {
        if (universalMetric[size]) filtered[size] = universalMetric[size];
      });
      return Object.keys(filtered).length > 0 ? filtered : universalMetric;
    }
    return universalMetric;
  }

  /**
   * The next size up (the "looser option"), or null when there is none or the customer is not
   * at the upper end of the recommended size's range.
   *
   * Shows ONLY when the customer's measurement is:
   *   1) Strictly above the midpoint of the recommended size range (never at the bottom/middle), AND
   *   2) Within the top 30% of the bracket width (e.g. 97-100 on a 90-100 range, or 97-98 on a 94-98 range).
   */
  function findAlternativeSize(entries, finalSize, minMargin) {
    const deciding = entries.filter((e) => e.ranges && e.ranges[finalSize] && e.size === finalSize);
    if (!deciding.length) return null;

    const keys = getSortedKeys(deciding[0].ranges);
    const pos = keys.indexOf(finalSize);
    const nextUp = pos !== -1 ? keys[pos + 1] : null;
    if (!nextUp) return null;

    const isAtUpperEnd = deciding.some((e) => {
      const [min, max] = e.ranges[finalSize];
      const span = Math.max(0, max - min);
      const mid = (min + max) / 2;
      // Threshold is 30% of bracket width (e.g. 3 cm on a 10 cm range, 1.2 cm on a 4 cm range),
      // or minMargin (1.5 cm for clothes, 1.0 cm for accessories).
      const allowedDistFromTop = Math.max(minMargin, span * ALT_SIZE_TOP_FRACTION);
      const distFromTop = max - e.eff;

      return e.eff > mid && distFromTop <= allowedDistFromTop;
    });

    return isAtUpperEnd ? { size: nextUp, direction: 'looser' } : null;
  }

  /**
   * ADDED: the two neighbouring sizes when the customer sits within BETWEEN_MARGIN_CM of their
   * recommended band's edge, else null. Read-only — it never influences which size is returned.
   * Mirrors the same "which metric decided the final size" rule used by getLargerSize() above.
   */
  function findBetweenSizes(primaryRanges, effPrimary, primarySize, secondaryRanges, effSecondary, secondarySize, finalSize) {
    const useSecondary = !!secondaryRanges && secondarySize === finalSize;
    const rangesFor = useSecondary ? secondaryRanges : primaryRanges;
    const eff = useSecondary ? effSecondary : effPrimary;
    const band = rangesFor && rangesFor[finalSize];
    if (!band) return null;

    const keys = getSortedKeys(rangesFor);
    const pos = keys.indexOf(finalSize);
    const upper = pos !== -1 && pos < keys.length - 1 ? keys[pos + 1] : null;
    const lower = pos > 0 ? keys[pos - 1] : null;

    if (upper && (band[1] - eff) <= BETWEEN_MARGIN_CM) return { smaller: finalSize, larger: upper };
    if (lower && (eff - band[0]) <= BETWEEN_MARGIN_CM) return { smaller: lower, larger: finalSize };
    return null;
  }

  /**
   * Height band for ANY size, even one missing from the height table (this fixes the 4XL bug)
   * or a combined size like "S/M" or "L/XL".
   * A missing size inherits the band of the nearest size in the table (preferring the smaller one).
   * Returns { range: [min, max], plateau: boolean } or null if the size is unknown to SIZES_ORDER.
   */
  function getHeightBand(heightRanges, size) {
    const cleanSize = normalizeSizeKey(size);
    const idx = SIZES_ORDER.indexOf(cleanSize);
    const known = Object.keys(heightRanges)
      .filter((s) => SIZES_ORDER.indexOf(s) !== -1)
      .sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b));

    if (idx === -1 || known.length === 0) return null;

    if (heightRanges[cleanSize]) {
      const pos = known.indexOf(cleanSize);
      const prev = pos > 0 ? heightRanges[known[pos - 1]] : null;
      return { range: heightRanges[cleanSize], plateau: sameRange(prev, heightRanges[cleanSize]) };
    }

    if (cleanSize.indexOf('/') !== -1) {
      const parts = cleanSize.split('/').map(normalizeSizeKey).filter((p) => heightRanges[p]);
      if (parts.length > 0) {
        const mins = parts.map((p) => heightRanges[p][0]);
        const maxs = parts.map((p) => heightRanges[p][1]);
        return { range: [Math.min.apply(null, mins), Math.max.apply(null, maxs)], plateau: false };
      }
    }

    let nearest = known[0];
    let bestDistance = Infinity;
    known.forEach((s) => {
      const sIdx = SIZES_ORDER.indexOf(s);
      const distance = Math.abs(sIdx - idx);
      if (distance < bestDistance || (distance === bestDistance && sIdx < idx)) {
        bestDistance = distance;
        nearest = s;
      }
    });

    // Extending upwards = girth-extension size (plateau). Extending downwards is not.
    return { range: heightRanges[nearest], plateau: idx > SIZES_ORDER.indexOf(nearest) };
  }

  /**
   * Main entry point.
   * @param {object} sizeData  parsed product JSON
   * @param {{height, primary, secondary}} input  raw values from the form (numbers or strings)
   * @returns {{status: 'ok'|'missing'|'invalid_height'|'not_found', ...}}
   */
  function computeRecommendation(sizeData, input) {
    const props = sizeData.garment_properties || {};
    const primaryKey = props.primary_metric || 'chest';
    const secondaryKey = props.secondary_metric || null;
    const isAccessory = ACCESSORY_METRICS.indexOf(primaryKey) !== -1;
    const gender = props.gender || (secondaryKey === 'hips' ? 'women' : 'men');
    const adj = resolveAdjustments(props, isAccessory);

    const height = toNumber(input.height);
    const primary = toNumber(input.primary);
    const secondary = toNumber(input.secondary);

    // --- Validation: height is required for everything except accessories -----------------
    const missingHeight = !isAccessory && !(height > 0);
    const missingPrimary = !(primary > 0);
    const missingSecondary = !!secondaryKey && !(secondary > 0);

    if (missingHeight || missingPrimary || missingSecondary) {
      return { status: 'missing', isAccessory, adjustments: adj };
    }
    if (!isAccessory && (height < MIN_HEIGHT_CM || height > MAX_HEIGHT_CM)) {
      return { status: 'invalid_height', isAccessory, adjustments: adj };
    }

    // --- Effective measurements (body + ease allowance from material / fit / layer) ----------
    const effPrimary = primary + adj.offset;
    const effSecondary = secondaryKey ? secondary + adj.offset : null;

    // --- Primary metric -----------------------------------------------------------------------
    const primaryRanges = getMetricRanges(sizeData, primaryKey, gender);
    const availableSizes = primaryRanges ? Object.keys(primaryRanges) : [];
    const primarySize = findBestSize(effPrimary, primaryRanges, adj.tolerance);

    if (!primarySize) {
      return { status: 'not_found', isAccessory, adjustments: adj };
    }

    let finalSize = primarySize;
    let secondarySize = null;
    let secondaryRanges = null;

    // --- Secondary metric ---------------------------------------------------------------------
    if (secondaryKey) {
      secondaryRanges = getMetricRanges(sizeData, secondaryKey, gender, availableSizes);
      secondarySize = findBestSize(effSecondary, secondaryRanges, adj.tolerance);

      if (!secondarySize) {
        return { status: 'not_found', isAccessory, adjustments: adj };
      }
      finalSize = getLargerSize(primarySize, secondarySize);
    }

    // --- Height warning (not for accessories) -------------------------------------------------
    let heightWarning = null; // 'short' = garment may be too short, 'long' = may be too long

    const hasProductHeightTable = !!(sizeData.body_measurements_cm &&
      sizeData.body_measurements_cm.height &&
      Object.keys(sizeData.body_measurements_cm.height).length > 0);
    const heightTolerance = hasProductHeightTable ? adj.heightTolerance : UNIVERSAL_HEIGHT_TOLERANCE;

    if (!isAccessory && heightTolerance != null) {
      // No availableSizes filter here: we want the full table so missing sizes can inherit from it.
      const heightRanges = getMetricRanges(sizeData, 'height', gender, null);
      const band = heightRanges ? getHeightBand(heightRanges, finalSize) : null;

      if (band) {
        const [min, max] = band.range;
        const tol = heightTolerance;

        if (height > max + tol) {
          heightWarning = 'short';
        } else if (height < min - tol) {
          const suppress = SUPPRESS_LONG_WARNING_ON_PLATEAU && band.plateau;
          if (!suppress) heightWarning = 'long';
        }
      }
    }

    const alternative = findAlternativeSize(
      [
        { ranges: primaryRanges, eff: effPrimary, size: primarySize },
        { ranges: secondaryRanges, eff: effSecondary, size: secondarySize }
      ],
      finalSize,
      isAccessory ? ALT_SIZE_MARGIN_ACCESSORY_CM : ALT_SIZE_MARGIN_CM
    );

    // ADDED: purely informational, does not affect `size`.
    // Suppressed when `alternative` already fires — at the TOP edge of a band the two lines say the
    // same thing ("size up"), so we keep only the more specific "looser fit" one. At the BOTTOM edge
    // `alternative` never fires, so this stays the only hint ("take the smaller size for a closer fit").
    const betweenSizes = alternative ? null : findBetweenSizes(
      primaryRanges, effPrimary, primarySize,
      secondaryRanges, effSecondary, secondarySize,
      finalSize
    );

    return {
      status: 'ok',
      size: finalSize,
      primarySize,
      secondarySize,
      heightWarning,
      alternative, // { size, direction: 'looser' } or null
      betweenSizes, // ADDED: { smaller, larger } or null
      isAccessory,
      gender,
      adjustments: adj
    };
  }

  /**
   * Data-quality check for one product's JSON. Returns a list of human-readable problems
   * (empty list = clean). Run automatically in the browser (console.warn) and usable in bulk.
   */
  function validateSizeData(sizeData) {
    const problems = [];
    const props = sizeData && sizeData.garment_properties;
    const body = (sizeData && sizeData.body_measurements_cm) || {};
    if (!props) return ['garment_properties missing'];

    const primary = props.primary_metric;
    const secondary = props.secondary_metric;
    const isAccessory = ACCESSORY_METRICS.indexOf(primary) !== -1;

    if (!primary) problems.push('primary_metric missing');
    if (!props.gender) problems.push('gender missing');
    if (primary && !body[primary]) problems.push(`no body_measurements_cm.${primary} for primary_metric`);
    if (secondary && !body[secondary]) problems.push(`no body_measurements_cm.${secondary} for secondary_metric`);

    if (!isAccessory) {
      ['material', 'fit', 'layer'].forEach((g) => {
        if (!props[g]) problems.push(`${g} missing (treated as neutral)`);
      });
      resolveAdjustments(props, false).warnings.forEach((w) => problems.push(w));
    }

    Object.keys(body).forEach((metric) => {
      const sanitized = sanitizeRanges(body[metric]) || {};
      const entries = Object.entries(sanitized);
      entries.forEach(([size, r]) => {
        if (SIZES_ORDER.indexOf(size) === -1) problems.push(`${metric}: unknown size "${size}"`);
        if (!Array.isArray(r) || r.length !== 2 || !(r[0] <= r[1])) problems.push(`${metric} ${size}: bad range ${JSON.stringify(r)}`);
      });
      const sorted = entries
        .filter(([sz]) => SIZES_ORDER.indexOf(sz) !== -1)
        .sort((x, y) => SIZES_ORDER.indexOf(x[0]) - SIZES_ORDER.indexOf(y[0]));
      for (let i = 1; i < sorted.length; i++) {
        const [pSize, pr] = sorted[i - 1];
        const [cSize, cr] = sorted[i];
        if (cr[0] < pr[0]) problems.push(`${metric}: ${cSize} starts below ${pSize} (sizes out of order)`);
        else if (cr[0] <= pr[1]) problems.push(`${metric}: ${pSize} and ${cSize} overlap (info: closest-centre size wins in the overlap)`);
        else if (cr[0] - pr[1] > 4) problems.push(`${metric}: ${(cr[0] - pr[1])} cm gap between ${pSize} and ${cSize}`);
      }
    });
    return problems;
  }

  root.SmartSizingEngine = {
    computeRecommendation,
    resolveAdjustments,
    findBestSize,
    getHeightBand,
    findAlternativeSize,
    findBetweenSizes, // ADDED (exposed for testing)
    validateSizeData,
    ADJUSTMENTS
  };

  // ===========================================================================
  // 3. DOM / DRAWER (browser only)
  // ===========================================================================

  if (typeof document === 'undefined') return;

  document.addEventListener('DOMContentLoaded', function () {
    const drawer = document.getElementById('smart-sizing-drawer');
    const openBtn = document.getElementById('smart-sizing-open-btn');
    const closeBtn = document.getElementById('smart-sizing-close-btn');
    const backdrop = document.getElementById('smart-sizing-backdrop');
    const container = document.getElementById('smart-sizing-calculator');
    const calcBtn = document.getElementById('calculate-size');

    if (!container || !calcBtn) return;

    // --- Drawer UI controller ---------------------------------------------------------------
    if (drawer && openBtn) {
      // Move drawer to document.body to avoid Shopify theme z-index / transform stacking traps
      document.body.appendChild(drawer);

      const openDrawer = function () {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        openBtn.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';

        // Focus the first visible, enabled input for smooth UX
        const firstInput = drawer.querySelector('input[type="number"]:not([disabled])');
        if (firstInput && firstInput.offsetParent !== null) {
          setTimeout(() => firstInput.focus(), 100);
        }
      };

      const closeDrawer = function () {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        openBtn.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        openBtn.focus();
      };

      openBtn.addEventListener('click', openDrawer);
      if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
      if (backdrop) backdrop.addEventListener('click', closeDrawer);

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
      });
    }

    // --- Data + form elements ---------------------------------------------------------------
    function loadSizeData() {
      const el = document.getElementById('size-guide-data');
      if (!el || !el.textContent.trim()) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (err) {
        console.error('Smart Sizing: Invalid JSON data', err);
        return null;
      }
    }

    const heightInput = document.getElementById('user-height-input');
    const primaryInput = document.getElementById('user-primary-input');
    const secondaryInput = document.getElementById('user-secondary-input');

    /**
     * Accessories (head / hand) don't need height: hide the field and disable the input.
     * To hide the label + input together, wrap them in an element with data-height-field.
     * Without that wrapper, the input and its <label for="user-height-input"> are hidden.
     */
    function applyHeightFieldVisibility(isAccessory) {
      if (!heightInput) return;
      const wrapper = heightInput.closest('[data-height-field]');
      const label = document.querySelector('label[for="user-height-input"]');
      const targets = wrapper ? [wrapper] : [heightInput, label];

      targets.forEach((node) => {
        if (node) node.hidden = isAccessory;
        if (node && node.style) node.style.display = isAccessory ? 'none' : '';
      });
      heightInput.disabled = isAccessory;
      heightInput.required = !isAccessory;
    }

    const initialData = loadSizeData();
    if (initialData) {
      validateSizeData(initialData).forEach((p) => console.warn('Smart Sizing data:', p));
      const metric = (initialData.garment_properties || {}).primary_metric || 'chest';
      applyHeightFieldVisibility(ACCESSORY_METRICS.indexOf(metric) !== -1);
    }

    // --- Calculate + render -----------------------------------------------------------------
    function calculateRecommendation(e) {
      if (e) e.preventDefault();

      const outputDiv = document.getElementById('recommended-size-output');
      if (!outputDiv) return;

      const sizeData = loadSizeData();
      if (!sizeData) return;

      const text = (attr) => container.getAttribute(attr) || '';
      const recommendText = text('data-recommend-text');
      const notFoundText = text('data-not-found-text');
      const missingFieldsText = text('data-missing-fields-text');
      // Shopify prints "Translation missing: ..." when a locale key doesn't exist; fall back in that case.
      const rawInvalidHeight = text('data-invalid-height-text');
      const invalidHeightText = rawInvalidHeight && !/^translation missing/i.test(rawInvalidHeight)
        ? rawInvalidHeight
        : missingFieldsText;
      const warningShortText = text('data-warning-short');
      const warningLongText = text('data-warning-long');

      const result = computeRecommendation(sizeData, {
        height: heightInput && !heightInput.disabled ? heightInput.value : null,
        primary: primaryInput ? primaryInput.value : null,
        secondary: secondaryInput ? secondaryInput.value : null
      });

      result.adjustments.warnings.forEach((w) => console.warn('Smart Sizing:', w));

      if (result.status === 'missing') {
        outputDiv.innerHTML = `<span style="color: #d9534f; font-weight: normal; font-size: 0.9em;">${missingFieldsText}</span>`;
        return;
      }
      if (result.status === 'invalid_height') {
        outputDiv.innerHTML = `<span style="color: #d9534f; font-weight: normal; font-size: 0.9em;">${invalidHeightText}</span>`;
        return;
      }
      if (result.status === 'not_found') {
        outputDiv.innerText = notFoundText;
        return;
      }

      let finalOutput = `${recommendText} <strong style="font-size: 1.2em; color: var(--green, #4ade80); background: var(--ink, #111); padding: 2px 8px;">${result.size}</strong>`;

      // "Also consider" size (needs the data-alt-* texts; silently skipped if they are missing)
      if (result.alternative) {
        const raw = text('data-alt-looser-text');
        if (raw && !/^translation missing/i.test(raw)) {
          const altHtml = raw.replace('{size}', `<strong>${result.alternative.size}</strong>`);
          finalOutput += `<br><span style="font-size: 0.85em; color: var(--mute, #666); font-weight: normal; display: block; margin-top: 6px;">${altHtml}</span>`;
        }
      }

      // ADDED: "between sizes" hint (needs data-between-text; silently skipped if missing)
      if (result.betweenSizes) {
        const raw = text('data-between-text');
        if (raw && !/^translation missing/i.test(raw)) {
          const msg = raw
            .replace(/\{smaller\}/g, result.betweenSizes.smaller)
            .replace(/\{larger\}/g, result.betweenSizes.larger)
            .replace(/\{size\}/g, result.size);
          finalOutput += `<br><span style="font-size: 0.85em; color: var(--mute, #666); font-weight: normal; display: block; margin-top: 6px;">${msg}</span>`;
        }
      }

      if (result.heightWarning === 'short') {
        finalOutput += `<br><span style="font-size: 0.85em; color: #d9534f; font-weight: normal; display: block; margin-top: 6px;">${warningShortText}</span>`;
      } else if (result.heightWarning === 'long') {
        finalOutput += `<br><span style="font-size: 0.85em; color: #f0ad4e; font-weight: normal; display: block; margin-top: 6px;">${warningLongText}</span>`;
      }

      outputDiv.innerHTML = finalOutput;
    }

    calcBtn.addEventListener('click', calculateRecommendation);

    container.querySelectorAll('input[type="number"]').forEach((input) => {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') calculateRecommendation(e);
      });
    });
  });
})(typeof window !== 'undefined' ? window : globalThis);